import {
  realtimeConfig,
  STREAM_PATHS,
  streamUrl,
  useSessionStore,
  type RealtimeConfig,
} from '@ctcells/core'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { createWebSocketConnection } from './connection.ts'
import { RealtimeContext } from './context.ts'
import {
  STREAM_NAMES,
  type RealtimeState,
  type StreamConnectionFactory,
  type StreamName,
  type StreamState,
  type StreamStatus,
} from './types.ts'

const DISCONNECTED: StreamState = { status: 'idle', lastMessageAt: null, stale: true }

const INITIAL: Record<StreamName, StreamState> = { events: DISCONNECTED, safety: DISCONNECTED }

/**
 * 테스트 환경에서는 셸이 스스로 연결하지 않는다. jsdom 이 진짜 소켓을 열려고 들면
 * 재연결 타이머가 테스트 뒤에 남는다. 테스트는 `createConnection` 으로 가짜를 주입한다.
 */
function defaultFactory(): StreamConnectionFactory | null {
  return import.meta.env.MODE === 'test' ? null : createWebSocketConnection
}

export interface RealtimeProviderProps {
  children: ReactNode
  /**
   * 연결을 만드는 방법. `null` 이면 **연결하지 않는다**(= 계속 stale = 제어 불가).
   * 🔴 목/실물이 갈리는 유일한 지점이다. 위쪽(상태 판정·게이트·화면)은 완전히 같은 코드를 탄다.
   */
  createConnection?: StreamConnectionFactory | null | undefined
  /** 채널별 주소. 기본은 `@ctcells/core` 의 `streamUrl(STREAM_PATHS[name])` */
  urls?: Partial<Record<StreamName, string>> | undefined
  /** 설정 주입(테스트·데모). 기본은 `realtimeConfig()` */
  config?: RealtimeConfig | undefined
}

/**
 * WebSocket 연결의 주인. 라우터보다 위에 마운트해 화면 전환에도 연결을 유지한다.
 * 연결 인스턴스는 앱 전체에서 각 1개(/events/stream · /safety/stream).
 *
 * 🔴 **stale 판정 두 축**
 * 1. 연결 상태 — `open` 이 아니면 stale. 임계 없이 즉시.
 * 2. 무소식 경과 — `config.staleThresholdMs` 가 **있을 때만**. `undefined` 면 **판정하지 않는다**
 *    (계약이 전역 기본을 아직 주지 않았다 · 추측값 금지 — `packages/core/src/realtimeConfig.ts`).
 *
 * 🔴 기본값은 「미연결 = stale = 제어 불가」다. 모르면 막는 쪽으로 기운다.
 */
export function RealtimeProvider({
  children,
  createConnection,
  urls,
  config,
}: RealtimeProviderProps) {
  const [streams, setStreams] = useState<Record<StreamName, StreamState>>(INITIAL)
  const sessionId = useSessionStore((state) => state.sessionId)

  /** 한 번이라도 `open` 이 된 채널. 그 채널이 stale 이 되면 «끊김»이다(첫 연결 중은 아니다). 상태라 렌더가 따라온다 */
  const [opened, setOpened] = useState<ReadonlySet<StreamName>>(() => new Set())
  const listenersRef = useRef<Map<StreamName, Set<(raw: unknown) => void>>>(
    new Map([
      ['events', new Set<(raw: unknown) => void>()],
      ['safety', new Set<(raw: unknown) => void>()],
    ]),
  )

  const factory = createConnection === undefined ? defaultFactory() : createConnection
  const eventsUrl = urls?.events ?? streamUrl(STREAM_PATHS.events)
  const safetyUrl = urls?.safety ?? streamUrl(STREAM_PATHS.safety)
  const resolvedConfig = config ?? realtimeConfig()

  useEffect(() => {
    // 연결을 만들지 않는 경우엔 상태를 건드리지 않는다. 표시값은 아래 useMemo 가 INITIAL 로 고정한다
    if (!factory) return

    const timers = new Map<StreamName, ReturnType<typeof setTimeout>>()
    let live = true

    const clearStaleTimer = (name: StreamName): void => {
      const timer = timers.get(name)
      if (timer !== undefined) {
        clearTimeout(timer)
        timers.delete(name)
      }
    }

    /** 🔴 임계가 없으면 타이머를 걸지 않는다 — 시간 축 판정 자체를 하지 않는다 */
    const armStaleTimer = (name: StreamName): void => {
      clearStaleTimer(name)
      const threshold = resolvedConfig.staleThresholdMs
      if (threshold === undefined) return
      timers.set(
        name,
        setTimeout(() => {
          if (!live) return
          setStreams((prev) => {
            const current = prev[name]
            if (current.stale) return prev
            return { ...prev, [name]: { ...current, stale: true } }
          })
        }, threshold),
      )
    }

    const handleStatus = (name: StreamName, status: StreamStatus): void => {
      if (!live || sessionId !== useSessionStore.getState().sessionId) return
      // 한 번이라도 열린 채널 — 그 뒤의 stale 만 「끊김」이다(`RealtimeState.outage`)
      if (status === 'open') {
        setOpened((prev) => (prev.has(name) ? prev : new Set([...prev, name])))
      }
      if (status === 'open') armStaleTimer(name)
      else clearStaleTimer(name)

      setStreams((prev) => {
        const current = prev[name]
        // open 이 아니면 즉시 stale. open 이면 연결이 새로 섰으니 stale 을 푼다
        const next: StreamState = {
          status,
          lastMessageAt: current.lastMessageAt,
          stale: status !== 'open',
        }
        if (
          current.status === next.status &&
          current.stale === next.stale &&
          current.lastMessageAt === next.lastMessageAt
        ) {
          return prev
        }
        return { ...prev, [name]: next }
      })
    }

    const handleMessage = (name: StreamName, raw: unknown): void => {
      if (!live || sessionId !== useSessionStore.getState().sessionId) return
      // 🔴 구독자에게는 «모든» 프레임을 그대로 흘린다. 여기서 캐시를 건드리지 않는다
      for (const listener of listenersRef.current.get(name) ?? []) listener(raw)

      armStaleTimer(name)
      const now = Date.now()
      setStreams((prev) => {
        const current = prev[name]
        const fresh =
          !current.stale &&
          current.lastMessageAt !== null &&
          now - current.lastMessageAt < resolvedConfig.activityUpdateMs
        // 같은 초 안의 연속 수신은 상태를 갱신하지 않는다(렌더 억제). ⚠ 폴링이 아니다
        if (fresh) return prev
        return { ...prev, [name]: { ...current, lastMessageAt: now, stale: false } }
      })
    }

    const connections = [
      factory({
        name: 'events',
        url: eventsUrl,
        onStatus: (status) => {
          handleStatus('events', status)
        },
        onMessage: (raw) => {
          handleMessage('events', raw)
        },
      }),
      factory({
        name: 'safety',
        url: safetyUrl,
        onStatus: (status) => {
          handleStatus('safety', status)
        },
        onMessage: (raw) => {
          handleMessage('safety', raw)
        },
      }),
    ]

    return () => {
      live = false
      for (const name of STREAM_NAMES) clearStaleTimer(name)
      for (const connection of connections) connection.close()
      setStreams(INITIAL)
    }
  }, [factory, eventsUrl, safetyUrl, resolvedConfig, sessionId])

  const subscribe = useCallback((name: StreamName, listener: (raw: unknown) => void) => {
    const set = listenersRef.current.get(name)
    set?.add(listener)
    return () => {
      set?.delete(listener)
    }
  }, [])

  const value = useMemo(() => {
    const active = factory ? streams : INITIAL
    const state: RealtimeState = {
      events: active.events,
      safety: active.safety,
      controlDisabled: active.events.stale || active.safety.stale,
      outage:
        (active.events.stale && opened.has('events')) ||
        (active.safety.stale && opened.has('safety')),
    }
    return { state, subscribe }
  }, [factory, streams, subscribe, opened])

  return <RealtimeContext value={value}>{children}</RealtimeContext>
}
